/**
 * Entitlements — directApiClient (LF-3216)
 *
 * `revalidatePath` calls dropped — invalidation now happens via React Query
 * at the hook layer (see hooks.ts + query-dependencies.ts `entitlement`).
 */

'use client';

import { directApiClient, DirectApiError } from '@/lib/api/direct-client';
import type {
  BulkCreateEntitlementsRequest,
  CreateEntitlementClassRequest,
  CreateEntitlementRequest,
  EntitlementClassResponse,
  EntitlementResponse,
  PrefillIntestacyRequest,
  ResolveEntitlementClassRequest,
  UpdateEntitlementClassMembersRequest,
  UpdateEntitlementRequest,
  AmountUpdate
} from './types';

interface ActionResult<T = void> {
  success: boolean;
  data?: T;
  error?: string;
}

function toErrorResult<T>(error: unknown): ActionResult<T> {
  if (error instanceof DirectApiError) {
    return { success: false, error: error.message };
  }
  return {
    success: false,
    error: error instanceof Error ? error.message : 'Request failed'
  };
}

// =============================================================================
// Mutations (writes)
// =============================================================================

export async function createEntitlementAction(
  matterId: number,
  data: CreateEntitlementRequest
): Promise<ActionResult<EntitlementResponse>> {
  try {
    const result = await directApiClient.post<EntitlementResponse>(
      `/matters/${matterId}/entitlements`,
      data
    );
    return { success: true, data: result };
  } catch (error) {
    return toErrorResult<EntitlementResponse>(error);
  }
}

export async function updateEntitlementAction(
  matterId: number,
  entitlementId: number,
  data: UpdateEntitlementRequest
): Promise<ActionResult<EntitlementResponse>> {
  try {
    const result = await directApiClient.patch<EntitlementResponse>(
      `/matters/${matterId}/entitlements/${entitlementId}`,
      data
    );
    return { success: true, data: result };
  } catch (error) {
    return toErrorResult<EntitlementResponse>(error);
  }
}

export async function deleteEntitlementAction(
  matterId: number,
  entitlementId: number
): Promise<ActionResult> {
  try {
    await directApiClient.delete(
      `/matters/${matterId}/entitlements/${entitlementId}`
    );
    return { success: true };
  } catch (error) {
    return toErrorResult(error);
  }
}

export async function bulkCreateEntitlementsAction(
  matterId: number,
  data: BulkCreateEntitlementsRequest
): Promise<ActionResult<EntitlementResponse[]>> {
  try {
    const result = await directApiClient.post<EntitlementResponse[]>(
      `/matters/${matterId}/entitlements/bulk`,
      data
    );
    return { success: true, data: result };
  } catch (error) {
    return toErrorResult<EntitlementResponse[]>(error);
  }
}

export async function prefillIntestacyAction(
  matterId: number,
  data: PrefillIntestacyRequest = {}
): Promise<ActionResult<EntitlementResponse[]>> {
  try {
    const result = await directApiClient.post<EntitlementResponse[]>(
      `/matters/${matterId}/entitlements/prefill-intestacy`,
      data
    );
    return { success: true, data: result };
  } catch (error) {
    return toErrorResult<EntitlementResponse[]>(error);
  }
}

// =============================================================================
// Entitlement classes
// =============================================================================

export async function createEntitlementClassAction(
  matterId: number,
  data: CreateEntitlementClassRequest
): Promise<ActionResult<EntitlementClassResponse>> {
  try {
    const result = await directApiClient.post<EntitlementClassResponse>(
      `/matters/${matterId}/entitlement-classes`,
      data
    );
    return { success: true, data: result };
  } catch (error) {
    return toErrorResult<EntitlementClassResponse>(error);
  }
}

export async function resolveEntitlementClassAction(
  matterId: number,
  entitlementClassId: number,
  data: ResolveEntitlementClassRequest
): Promise<ActionResult<EntitlementClassResponse>> {
  try {
    const result = await directApiClient.post<EntitlementClassResponse>(
      `/matters/${matterId}/entitlement-classes/${entitlementClassId}/resolve`,
      data
    );
    return { success: true, data: result };
  } catch (error) {
    return toErrorResult<EntitlementClassResponse>(error);
  }
}

export async function updateEntitlementClassMembersAction(
  matterId: number,
  entitlementClassId: number,
  data: UpdateEntitlementClassMembersRequest
): Promise<ActionResult<EntitlementClassResponse>> {
  try {
    const result = await directApiClient.patch<EntitlementClassResponse>(
      `/matters/${matterId}/entitlement-classes/${entitlementClassId}/members`,
      data
    );
    return { success: true, data: result };
  } catch (error) {
    return toErrorResult<EntitlementClassResponse>(error);
  }
}

export async function deleteEntitlementClassAction(
  matterId: number,
  entitlementClassId: number
): Promise<ActionResult> {
  try {
    await directApiClient.delete(
      `/matters/${matterId}/entitlement-classes/${entitlementClassId}`
    );
    return { success: true };
  } catch (error) {
    return toErrorResult(error);
  }
}

// =============================================================================
// Inventory realised values (lives under inventory-items route)
// =============================================================================

interface UpdateRealisedValuesResponse {
  updatedCount: number;
  updatedItems: {
    id: number;
    type: string;
    realisedValue: number;
    estimatedValue: number;
  }[];
}

export async function updateRealisedValuesAction(
  matterId: number,
  updates: AmountUpdate[]
): Promise<ActionResult<UpdateRealisedValuesResponse>> {
  try {
    // Drop a null `receivedAt` rather than sending it: "no date given" and
    // "date explicitly cleared" mean the same thing to the backend (the amount
    // saves, no receipt is written), and omitting the key keeps the request
    // body honest about what the lawyer actually supplied.
    const items = updates.map(({ receivedAt, ...rest }) =>
      receivedAt ? { ...rest, receivedAt } : rest
    );

    const result = await directApiClient.patch<UpdateRealisedValuesResponse>(
      `/matters/${matterId}/inventory-items/realised-values`,
      { items }
    );
    return { success: true, data: result };
  } catch (error) {
    return toErrorResult<UpdateRealisedValuesResponse>(error);
  }
}
